import { ref, computed, type Ref } from 'vue'
import { App } from '.'

/**
 * 히스토리 아이템 인터페이스
 */
interface HistoryItem {
  id: string
  description: string
  timestamp: number
  // data: any
  undoAction: () => Promise<void> | void
  redoAction: () => Promise<void> | void
}

/**
 * 간단한 Undo/Redo 시스템
 * 최대 20개의 히스토리를 관리합니다
 */
class UndoRedoManager {
  private static instance: UndoRedoManager | null = null
  private undoStack = ref<HistoryItem[]>([])
  private redoStack = ref<HistoryItem[]>([])
  private maxHistorySize = 10
  // batch 관련 필드
  private batchDepth = 0
  private batchItems: HistoryItem[] = []
  private batchTimeoutId: ReturnType<typeof setTimeout> | null = null
  // batch가 열린 후 이 시간(ms) 내에 endBatch가 호출되지 않으면 자동 cancel
  private static readonly BATCH_TIMEOUT_MS = 10_000

  private constructor() {}

  /**
   * 싱글톤 인스턴스를 반환합니다
   */
  static getInstance(): UndoRedoManager {
    if (!UndoRedoManager.instance) {
      UndoRedoManager.instance = new UndoRedoManager()
    }
    return UndoRedoManager.instance
  }

  // batch 진행 중 여부
  isBatching(): boolean {
    return this.batchDepth > 0
  }

  // 여러 execute() 호출을 하나의 undo 엔트리로 병합
  startBatch(): void {
    this.batchDepth++
    // 최외곽 batch 시작 시 timeout 설정
    if (this.batchDepth === 1) {
      this.clearBatchTimeout()
      this.batchTimeoutId = setTimeout(() => {
        if (this.batchDepth > 0) {
          console.warn(
            `[UndoRedo] Batch timed out after ${UndoRedoManager.BATCH_TIMEOUT_MS}ms — force cancelling (${this.batchItems.length} items lost)`,
          )
          this.batchDepth = 0
          this.batchItems = []
          this.batchTimeoutId = null
        }
      }, UndoRedoManager.BATCH_TIMEOUT_MS)
    }
  }

  endBatch(description: string): void {
    if (this.batchDepth <= 0) return
    this.batchDepth--
    if (this.batchDepth > 0) return
    this.clearBatchTimeout()
    if (this.batchItems.length === 0) return

    const items = [...this.batchItems]
    this.batchItems = []

    const compoundItem: HistoryItem = {
      id: `batch_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      description,
      timestamp: Date.now(),
      undoAction: async () => {
        for (let i = items.length - 1; i >= 0; i--) {
          await items[i].undoAction()
        }
      },
      redoAction: async () => {
        for (const item of items) {
          await item.redoAction()
        }
      },
    }

    this.undoStack.value.push(compoundItem)
    if (this.undoStack.value.length > this.maxHistorySize) {
      this.undoStack.value.shift()
    }
    this.redoStack.value = []
    console.info(`[UndoRedo] Batch completed: ${description} (${items.length} items)`)
  }

  // batch를 items 없이 취소 (batch 중 아무 execute도 없었을 때)
  cancelBatch(): void {
    if (this.batchDepth <= 0) return
    this.batchDepth--
    if (this.batchDepth === 0) {
      this.clearBatchTimeout()
      this.batchItems = []
    }
  }

  private clearBatchTimeout(): void {
    if (this.batchTimeoutId !== null) {
      clearTimeout(this.batchTimeoutId)
      this.batchTimeoutId = null
    }
  }

  /**
   * 새로운 액션을 히스토리에 추가합니다
   */
  execute(
    description: string,
    undoAction: () => Promise<void> | void,
    redoAction: () => Promise<void> | void,
    // data?: any,
  ): void {
    try {
      // 히스토리에 추가
      const historyItem: HistoryItem = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        description,
        timestamp: Date.now(),
        // data: data || null,
        undoAction,
        redoAction,
      }

      // batch 진행 중이면 batchItems에 저장
      if (this.batchDepth > 0) {
        this.batchItems.push(historyItem)
        console.info(`[UndoRedo] Batched: ${description}`)
        return
      }

      this.undoStack.value.push(historyItem)

      // 히스토리 크기 제한
      if (this.undoStack.value.length > this.maxHistorySize) {
        this.undoStack.value.shift()
      }

      // 새 액션이 실행되면 redo 스택 클리어
      this.redoStack.value = []

      console.info(`[UndoRedo] Executed: ${description}`)
    } catch (error) {
      console.error(`[UndoRedo] Failed to execute: ${description}`, error)
      throw error
    }
  }

  /**
   * 마지막 액션을 되돌립니다
   */
  async undo(): Promise<boolean> {
    const toastInstance = App.toastInstance
    if (!this.canUndo()) {
      console.warn('[UndoRedo] Cannot undo: No items in undo stack')
      return false
    }

    const item = this.undoStack.value.pop()!

    try {
      // 데이터 undo 실행
      await item.undoAction()
      this.redoStack.value.push(item)

      // redo 스택도 크기 제한
      if (this.redoStack.value.length > this.maxHistorySize) {
        this.redoStack.value.shift()
      }

      toastInstance.add('success', `${item.description} 되돌려졌습니다.`)
      return true
    } catch (error) {
      console.error(`[UndoRedo] Failed to undo: ${item.description}`, error)
      // 실패하면 다시 undo 스택에 추가
      this.undoStack.value.push(item)
      return false
    }
  }

  /**
   * 마지막으로 되돌린 액션을 다시 실행합니다
   */
  async redo(): Promise<boolean> {
    const toastInstance = App.toastInstance
    if (!this.canRedo()) {
      console.warn('[UndoRedo] Cannot redo: No items in redo stack')
      return false
    }

    const item = this.redoStack.value.pop()!

    try {
      // 데이터 redo 실행
      await item.redoAction()
      this.undoStack.value.push(item)

      // undo 스택도 크기 제한
      if (this.undoStack.value.length > this.maxHistorySize) {
        this.undoStack.value.shift()
      }

      toastInstance.add('success', `${item.description} 다시 실행되었습니다.`)
      return true
    } catch (error) {
      console.error(`[UndoRedo] Failed to redo: ${item.description}`, error)
      // 실패하면 다시 redo 스택에 추가
      this.redoStack.value.push(item)
      return false
    }
  }

  /**
   * Undo 가능 여부를 확인합니다
   */
  canUndo(): boolean {
    return this.undoStack.value.length > 0
  }

  /**
   * Redo 가능 여부를 확인합니다
   */
  canRedo(): boolean {
    return this.redoStack.value.length > 0
  }

  /**
   * 모든 히스토리를 초기화합니다
   */
  clear(): void {
    this.undoStack.value = []
    this.redoStack.value = []
    console.info('[UndoRedo] History cleared')
  }

  /**
   * 현재 히스토리 상태를 반환합니다
   */
  getState(): {
    canUndo: boolean
    canRedo: boolean
    undoCount: number
    redoCount: number
    lastAction?: string
  } {
    const lastAction =
      this.undoStack.value.length > 0
        ? this.undoStack.value[this.undoStack.value.length - 1].description
        : undefined

    return {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoCount: this.undoStack.value.length,
      redoCount: this.redoStack.value.length,
      lastAction,
    }
  }

  /**
   * 디버깅용 히스토리 정보를 반환합니다
   */
  getHistoryInfo(): {
    undoStack: Array<{ id: string; description: string; timestamp: number }>
    redoStack: Array<{ id: string; description: string; timestamp: number }>
  } {
    return {
      undoStack: this.undoStack.value.map((item: HistoryItem) => ({
        id: item.id,
        description: item.description,
        timestamp: item.timestamp,
      })),
      redoStack: this.redoStack.value.map((item: HistoryItem) => ({
        id: item.id,
        description: item.description,
        timestamp: item.timestamp,
      })),
    }
  }
}

/**
 * Vue Composable for UndoRedo
 */
export function useUndoRedo(): {
  execute: (
    description: string,
    executeAction: () => Promise<void> | void,
    undoAction: () => Promise<void> | void,
  ) => void
  undo: () => Promise<boolean>
  redo: () => Promise<boolean>
  canUndo: Ref<boolean>
  canRedo: Ref<boolean>
  clear: () => void
  startBatch: () => void
  endBatch: (description: string) => void
  cancelBatch: () => void
  isBatching: () => boolean
  getState: () => {
    canUndo: boolean
    canRedo: boolean
    undoCount: number
    redoCount: number
    lastAction?: string
  }
  getHistoryInfo: () => {
    undoStack: Array<{ id: string; description: string; timestamp: number }>
    redoStack: Array<{ id: string; description: string; timestamp: number }>
  }
} {
  const manager = UndoRedoManager.getInstance()

  const canUndo = computed(() => manager.canUndo())
  const canRedo = computed(() => manager.canRedo())

  return {
    // 액션 실행
    execute: (
      description: string,
      executeAction: () => Promise<void> | void,
      undoAction: () => Promise<void> | void,
      // data?: any,
    ): void => manager.execute(description, executeAction, undoAction),

    // Undo/Redo
    undo: (): Promise<boolean> => manager.undo(),
    redo: (): Promise<boolean> => manager.redo(),

    // 상태 확인
    canUndo,
    canRedo,

    // Batch: 여러 execute를 하나의 undo 엔트리로 병합
    startBatch: (): void => manager.startBatch(),
    endBatch: (description: string): void => manager.endBatch(description),
    cancelBatch: (): void => manager.cancelBatch(),
    isBatching: (): boolean => manager.isBatching(),

    // 유틸리티
    clear: (): void => manager.clear(),
    getState: (): {
      canUndo: boolean
      canRedo: boolean
      undoCount: number
      redoCount: number
      lastAction?: string
    } => manager.getState(),
    getHistoryInfo: (): {
      undoStack: Array<{ id: string; description: string; timestamp: number }>
      redoStack: Array<{ id: string; description: string; timestamp: number }>
    } => manager.getHistoryInfo(),
  }
}

// 전역에서 사용할 수 있는 인스턴스 export
export const undoRedoManager = UndoRedoManager.getInstance()
